import { type ElementStyles, css, Observable, type Subscriber } from '@microsoft/fast-element';
import type { HoverIconDefinition, HoverIcon } from './HoverIcon';
import { unpin } from 'owa-locstrings/lib/strings/unpin.locstring.json';
import { pinTitle } from 'owa-mail-list-item-shared/lib/utils/getHoverActionIcon.locstring.json';
import { resetFocus } from 'owa-mail-focus-manager';
import { lazyToggleRowPinnedState } from 'owa-mail-triage-action';
import { PinFilled, PinRegular } from '@fluentui/react-icons/fonts';

const PinRegularFontGlyph = PinRegular.codepoint;
const PinFilledFontGlyph = PinFilled.codepoint;
const PinHoverIconStyles: ElementStyles = css`
    .icon:before {
        content: '${PinRegularFontGlyph}';
        color: var(--colorNeutralForeground3);
    }

    :host(:hover) .icon:before,
    :host(:active) .icon:before {
        content: '${PinFilledFontGlyph}';
        color: var(--customBlueForeground1);
    }
`;
const UnpinHoverIconStyles: ElementStyles = css`
    .icon:before {
        content: '${PinFilledFontGlyph}';
        color: var(--hoverColor, var(--customBlueForeground1));
    }

    :host(:hover),
    :host(:active) {
        --hoverColor: var(--customActiveBlueForegroundHover1);
    }
`;

const subscribers = new WeakMap<Readonly<HoverIcon>, Subscriber>();

function getSubscriber(subject: Readonly<HoverIcon>): Subscriber {
    const subscriber = subscribers.get(subject) ?? {
        handleChange: applyStyles,
    };
    subscribers.set(subject, subscriber);
    return subscriber;
}

function applyStyles(subject: Readonly<HoverIcon>) {
    if (subject.isPinned) {
        subject.$fastController.addStyles(UnpinHoverIconStyles);
        subject.$fastController.removeStyles(PinHoverIconStyles);
    } else {
        subject.$fastController.addStyles(PinHoverIconStyles);
        subject.$fastController.removeStyles(UnpinHoverIconStyles);
    }
}

export const PinUnpin: HoverIconDefinition = {
    title(this: Readonly<HoverIcon>) {
        return this.isPinned ? unpin : pinTitle;
    },
    connectedCallback(this: Readonly<HoverIcon>) {
        applyStyles(this);
        Observable.getNotifier(this).subscribe(getSubscriber(this), 'isPinned');
    },
    disconnectedCallback(this: Readonly<HoverIcon>) {
        Observable.getNotifier(this).unsubscribe(getSubscriber(this), 'isPinned');
    },
    onClick(this: HoverIcon) {
        lazyToggleRowPinnedState.importAndExecute(this.rowKey, this.tableViewId, 'Hover');
        // Reset focus after the stitch to pin the item is raised
        resetFocus('MailListItemIconBarPinned');
    },
};
